// Libraries
import React from 'react';

// Supermove
import {Icon, IconSource, Space, Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {ResponsiveType, usePresentationContext, useResponsive} from '@supermove/hooks';
import {ProjectModel, Project} from '@supermove/models';
import {Environment} from '@supermove/sdk';
import {colors} from '@supermove/styles';
import {ViewStyleProp} from '@supermove/styles/types';
import {Datetime, openNewTab} from '@supermove/utils';

// App
import IconButton from '@shared/design/components/IconButton';
import SkeletonLoader from '@shared/design/components/SkeletonLoader';
import Text from '@shared/design/components/Text';
import useViewerRoleQuery from '@shared/modules/App/hooks/useViewerRoleQuery';
import ProjectHeaderActions from '@shared/modules/Project/components/ProjectHeaderActions';
import ProjectStatusBadge from '@shared/modules/Project/components/ProjectStatusBadge';
import ProjectDetailsBlockWrapper from '@shared/modules/Project/components/widgets/components/ProjectDetailsBlockWrapper';
import ProjectOverviewFields from '@shared/modules/Project/components/widgets/components/ProjectOverviewFields';
import ProjectTagsList from '@shared/modules/Project/components/widgets/components/ProjectTagsList';
import ProjectTypeLabel from '@shared/modules/Project/components/widgets/components/ProjectTypeLabel';

const Row = Styled.View`
  flex-direction: row;
  align-items: center;
  flex-wrap: wrap;
`;

const ProjectNameButton = Styled.ButtonV2`
`;

const ProjectBadgeRightPlaceholder = Styled.View`
  flex: 1;
`;

const WidgetOverviewSection = Styled.View`
`;

const SkeletonComponent = () => {
  const responsive = useResponsive();

  return (
    <React.Fragment>
      <SkeletonLoader
        height={
          responsive.desktop
            ? SkeletonLoader.HEIGHT.Typography.Subheading
            : SkeletonLoader.HEIGHT.Typography.Heading2
        }
        width={200}
      />
      <Space height={16} />
      <SkeletonLoader
        height={
          responsive.desktop
            ? SkeletonLoader.HEIGHT.Typography.Body
            : SkeletonLoader.HEIGHT.Typography.Subheading
        }
        width={160}
      />
      <Space height={8} />
      <SkeletonLoader
        height={
          responsive.desktop
            ? SkeletonLoader.HEIGHT.Typography.Micro
            : SkeletonLoader.HEIGHT.Typography.Body
        }
        width={200}
      />
      <Space height={24} />
      <SkeletonLoader
        height={
          responsive.desktop
            ? SkeletonLoader.HEIGHT.Typography.Body
            : SkeletonLoader.HEIGHT.Typography.Subheading
        }
        width={200}
      />
      <Space height={20} />
      <ProjectDetailLoading icon={Icon.Calendar} />
      <ProjectDetailLoading icon={Icon.WeightHanging} />
      <ProjectDetailLoading icon={Icon.RulerTriangle} />
      <ProjectDetailLoading icon={Icon.MapPin} />
    </React.Fragment>
  );
};

const ProjectDetailLoading = ({icon}: {icon: IconSource}) => {
  const responsive = useResponsive();

  return (
    <React.Fragment>
      <Space height={8} />
      <Row>
        <IconButton source={icon} isSecondary isDisplayOnly />
        <Space width={8} />
        <SkeletonLoader
          height={
            responsive.desktop
              ? SkeletonLoader.HEIGHT.Typography.Body
              : SkeletonLoader.HEIGHT.Typography.Subheading
          }
          width={80}
        />
      </Row>
    </React.Fragment>
  );
};

const ProjectOverviewWidgetSection = ({
  project,
  refetch,
  responsive,
  headerActions = null,
  appName,
}: {
  project: ProjectModel;
  refetch: () => void;
  responsive: ResponsiveType;
  headerActions?: React.ReactNode;
  appName?: string;
}) => {
  const {isOfficeAppEnabled} = project.organization;
  const handleProjectPress = () => {
    const baseUrl = appName === 'Manager' ? '' : Environment.getAppUrl({useLocalUrl: true});
    openNewTab(`${baseUrl}${project.isStorage ? '/storage' : ''}/projects/${project.uuid}`);
  };
  const projectStyles = isOfficeAppEnabled
    ? ({color: colors.blue.interactive, textDecorationLine: 'underline'} as const)
    : {};

  return (
    <WidgetOverviewSection>
      <Row style={{flex: 1, justifyContent: 'space-between'}}>
        <ProjectNameButton onPress={isOfficeAppEnabled ? handleProjectPress : undefined}>
          <Text.Subheading style={projectStyles}>{Project.getName(project)}</Text.Subheading>
        </ProjectNameButton>
        {headerActions}
      </Row>
      <Space height={16} />
      <Row>
        <ProjectStatusBadge project={project} style={{paddingVertical: 0, height: 24}} isEditable />
        <ProjectBadgeRightPlaceholder />
      </Row>
      <Space height={16} />
      {project.client ? (
        <React.Fragment>
          <ProjectOverviewFields.ClientName
            project={project}
            refetch={refetch}
            responsive={responsive}
          />
          <Space height={8} />
        </React.Fragment>
      ) : null}
      {project.client?.primaryContact ? (
        <React.Fragment>
          <ProjectOverviewFields.ClientEmail
            project={project}
            refetch={refetch}
            responsive={responsive}
          />
          <Space height={8} />
          <ProjectOverviewFields.ClientPhoneNumber
            project={project}
            refetch={refetch}
            responsive={responsive}
          />
          <Space height={8} />
        </React.Fragment>
      ) : null}
      <ProjectOverviewFields.Company project={project} responsive={responsive} />
      <Space height={8} />
      <ProjectOverviewFields.ProjectType project={project} />
    </WidgetOverviewSection>
  );
};

const ProjectDetailsOverviewBlockContent = ({
  project,
  refetch,
  headerActions,
  handleClose,
  handleOpenProjectLocations,
}: {
  project: ProjectModel;
  refetch: () => void;
  headerActions?: React.ReactNode;
  handleClose?: () => void;
  handleOpenProjectLocations?: () => void;
}) => {
  const responsive = useResponsive();
  const {appName, isWidget} = usePresentationContext();

  return (
    <React.Fragment>
      {isWidget ? (
        <ProjectOverviewWidgetSection
          project={project}
          refetch={refetch}
          responsive={responsive}
          headerActions={headerActions}
          appName={appName}
        />
      ) : (
        <Text.Heading2>{project.name || project.client.name}</Text.Heading2>
      )}
      <Space height={8} />
      {isWidget ? null : (
        <Row>
          <Text.Micro style={{marginRight: 24, marginBottom: 8}}>
            {project.organization.name}
          </Text.Micro>
          <ProjectTypeLabel projectType={project.projectType} style={{marginBottom: 8}} />
        </Row>
      )}
      {project.isCancelled && (
        <React.Fragment>
          <Space height={8} />
          <Text.Micro
            style={{marginRight: 24, marginBottom: 8}}
          >{`Cancelled at: ${Datetime.convertToDisplayDatetime(project.cancelledAt)}`}</Text.Micro>
        </React.Fragment>
      )}
      {isWidget ? (
        <ProjectOverviewFields.Description project={project} onSuccess={refetch} />
      ) : project.description ? (
        <React.Fragment>
          <Space height={8} />
          <Text.Body>{project.description}</Text.Body>
        </React.Fragment>
      ) : null}
      {project.organization.isOfficeAppEnabled ? (
        <React.Fragment>
          <Space height={16} />
          <ProjectTagsList project={project} refetch={refetch} />
          <Space height={16} />
        </React.Fragment>
      ) : (
        <Space height={8} />
      )}
      <ProjectOverviewFields.ProjectData
        project={project}
        isWidget={isWidget}
        refetch={refetch}
        responsive={responsive}
        handleOpenProjectLocations={handleOpenProjectLocations}
        handleClose={handleClose}
      />
    </React.Fragment>
  );
};

const ProjectDetailsOverviewBlock = ({
  projectUuid,
  index,
  handleOpenProjectLocations,
  handleClose,
  refetch: refetchParent,
  style,
}: {
  projectUuid: string;
  index: number;
  // TODO(jay): This should be moved to shared as well, leaving it to the parent for now
  handleOpenProjectLocations?: () => void;
  handleClose?: () => void;
  refetch: () => void;
  style?: ViewStyleProp;
}) => {
  const {isSuperAdmin} = useViewerRoleQuery();
  const isOfficeApp = Environment.isOfficeApp();

  return (
    <ProjectDetailsBlockWrapper
      key={projectUuid}
      query={ProjectDetailsOverviewBlock.query}
      queryVariables={{projectUuid}}
      index={index}
      SkeletonComponent={SkeletonComponent}
      style={style}
    >
      {({data, refetch}: {data: {project: ProjectModel}; refetch: () => void}) => {
        return (
          <ProjectDetailsOverviewBlockContent
            project={data.project}
            refetch={() => {
              refetch();
              refetchParent?.();
            }}
            headerActions={
              !isOfficeApp || data.project.isStorage ? undefined : (
                <ProjectHeaderActions
                  project={data.project}
                  refetch={refetch}
                  isSuperUser={isSuperAdmin}
                />
              )
            }
            handleOpenProjectLocations={handleOpenProjectLocations}
            handleClose={handleClose}
          />
        );
      }}
    </ProjectDetailsBlockWrapper>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
ProjectDetailsOverviewBlock.query = gql`
  ${ProjectTypeLabel.fragment}
  ${Project.getName.fragment}
  ${ProjectStatusBadge.fragment}
  ${ProjectTagsList.fragment}
  ${ProjectOverviewFields.Company.fragment}
  ${ProjectOverviewFields.ClientName.fragment}
  ${ProjectOverviewFields.ClientEmail.fragment}
  ${ProjectOverviewFields.ClientPhoneNumber.fragment}
  ${ProjectOverviewFields.ProjectType.fragment}
  ${ProjectOverviewFields.Description.fragment}
  ${ProjectOverviewFields.ProjectData.fragment}
  ${ProjectHeaderActions.fragment}

  query ProjectDetailsOverviewBlock($projectUuid: String!) {
    project(uuid: $projectUuid) {
      id
      uuid
      description
      isCancelled
      isStorage
      cancelledAt
      organization {
        id
        name
        isOfficeAppEnabled
        features {
          isEnabledSalesLeadsListPage: isEnabled(feature: "SALES_LEADS_LIST_PAGE")
        }
      }
      client {
        id
        name
        primaryContact {
          id
          fullName
          email
          phoneNumber
        }
      }
      projectType {
        id
        color
        name
      }
      ...ProjectTypeLabel
      ...Project_getName
      ...ProjectStatusBadge
      ...ProjectTagsList
      ...ProjectOverviewFields_Company
      ...ProjectOverviewFields_ClientName
      ...ProjectOverviewFields_ClientEmail
      ...ProjectOverviewFields_ClientPhoneNumber
      ...ProjectOverviewFields_ProjectType
      ...ProjectOverviewFields_Description
      ...ProjectOverviewFields_ProjectData
      ...ProjectHeaderActions
    }
  }
`;

export default ProjectDetailsOverviewBlock;
